<template>
  <div :class="bem({'has-shipping': true})">
    <VisionProgressBar
      v-if="shopifyStore.freeShippingPromo"
      :class="bem('shipping-progress')"
      :progress="freeShippingProgress.complete ? 100 : freeShippingProgress.percent"
      :rounded="false"
    />

    <CartSidebarInfo :has-free-shipping="freeShippingProgress.complete" />
    <VisionButton
      :class="bem('cta')"
      :spa-link="true"
      :to="shopifyStore.cart.checkoutUrl"
      :square="true"
      background-colour="gradient"
    >
      <template #content>
        <div :class="bem('cta-inner')">
          <span>Checkout</span>
          <div :class="bem('cta-totals', {'is-loading': shopifyStore.loading})">
            <span
              v-if="cartTotals.discounted"
              :class="bem('cta-totals-original-price')"
            >
              <s>{{ $tcl(cartTotals.subtotal) }}</s>
            </span>
            <span>{{ cartTotals.total ? $tcl(cartTotals.total) : "FREE" }}</span>
          </div>
        </div>
      </template>
    </VisionButton>
    <PaymentIcons
      :methods="paymentMethods"
      size="small"
    />
  </div>
</template>

<script setup lang="ts">
import {
  getPaymentMethods,
  type PaymentMethodsResponse,
} from '~/graphql/shopify/queries/cart'

const bem = useBEM('CartSidebarCollateral')
const shopifyStore = useShopifyStore() as any

interface CartTotal {
  subtotal: number,
  total: number,
  discounted: boolean
}

const paymentMethods: any = ref([])

const cartTotals = computed<CartTotal>(() => {
  const subtotal = Number(shopifyStore.cart.cost.subtotalAmount.amount)
  const total = Number(shopifyStore.cart.cost.totalAmount.amount)
  const discounted = subtotal > total
  return {
    subtotal,
    total,
    discounted
  }
})

const freeShippingProgress = computed(() => {
  if (shopifyStore.freeShippingPromo?.thresholdSummary?.thresholdForDiscountMet) {
    return {
      percent: 100,
      complete: true
    }
  }
  
  const complete = shopifyStore.freeShippingPromo?.thresholdMet
  const percent = 100 - ((shopifyStore.freeShippingPromo?.remainingSpend / shopifyStore.freeShippingPromo?.threshold) * 100)
  return {
    percent,
    complete
  }
})

async function fetchPaymentMethods() {
  const { shopifyRequest } = useShopifyClient()
  const response = await shopifyRequest<PaymentMethodsResponse>(
    'get-payment-methods',
    getPaymentMethods
  )

  if (response) {
    const methods: string[] =
      ['paypal'].concat(response.shop.paymentSettings.supportedDigitalWallets).concat(
        response.shop.paymentSettings.acceptedCardBrands
      )
    paymentMethods.value = methods
      .map(method => method.replace('_', '')
      .toLowerCase())
      // Hard exclude these payment methods
      .filter((method: string) => method !== 'dinersclub')
      .filter((method: string) => method !== 'discover')
  }
}

onMounted(() => {
  fetchPaymentMethods()
})
</script>

<style lang="postcss">
.CartSidebarCollateral {
  @apply grid grid-cols-1 gap-4 pb-4;
}

.CartSidebarCollateral__shipping-progress {
  --gradient: rgb(65 182 117);
  @apply bg-green bg-opacity-[.24] h-0.5;
}

.CartSidebarCollateral__cta {
  @apply h-16 mx-[var(--cart-inline-padding)];
}

.CartSidebarCollateral__cta-inner {
  @apply flex justify-between;
}

.CartSidebarCollateral__cta-totals {
  @apply flex items-center gap-2 overflow-hidden;

  span {
    @apply transition-all duration-300;
  }

  &--is-loading {
    span {
      @apply translate-y-full;
    }
  }
}

.CartSidebarCollateral__cta-totals-original-price {
  @apply font-normal opacity-[.72] delay-[50ms];
}
</style>
